import React, { useState } from "react";
import { useDispatch } from "react-redux";
import Breadcrumb from "../../../components/layout/Breadcrumb";
import { createPolicy } from "../../../redux/slice/policiesSlice";
import { useNavigate } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import { Editor } from '@tinymce/tinymce-react';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useHasPermission } from "../../../hooks/useHasPermission";

export default function PolicyCreate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({
    title: false,
    content: false,
  });
  const [title, setTitle] = useState("");
  const [contentHtml, setContentHtml] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [version] = useState("");
  const canEditPolicy = useHasPermission("Policies-manage");

  const tinyConfig = {
    height: 500,
    menubar: "file edit view insert format tools table help",
    branding: false,
    promotion: false,
    statusbar: true,
    resize: true,

    plugins: [
      "advlist",
      "autolink",
      "lists",
      "link",
      "image",
      "charmap",
      "preview",
      "anchor",
      "searchreplace",
      "visualblocks",
      "code",
      "fullscreen",
      "insertdatetime",
      "media",
      "table",
      "help",
      "wordcount"
    ],

    toolbar:
      "undo redo | blocks fontfamily fontsize | " +
      "bold italic underline strikethrough | " +
      "image |" +
      "alignleft aligncenter alignright alignjustify | " +
      "bullist numlist outdent indent | " +
      "link  media table | " +
      "searchreplace preview fullscreen code | help",

    content_style:
      "body { font-family: Helvetica, Arial, sans-serif; font-size:14px; line-height: 1.7; }",

    image_title: false,
    image_dimensions: true,
    image_advtab: true,
    image_alt: false,
    file_picker_types: "image",

    file_picker_callback: (callback, value, meta) => {
      if (meta.filetype !== "image") return;
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = () => {
        const file = input.files[0];
        const reader = new FileReader();
        reader.onload = () => {
          callback(reader.result, {
            alt: file.name,
          });
        };
        reader.readAsDataURL(file);
      };
      input.click();
    },
    images_upload_handler: (blobInfo) =>
      new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(blobInfo.blob());
      }),
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isTitleEmpty = !title.trim();
    const isContentEmpty = !contentHtml?.trim();
    if (isTitleEmpty || isContentEmpty) {
      setErrors({
        title: isTitleEmpty,
        content: isContentEmpty,
      });
      toast.error("Please fill all required fields.", { className: 'toast-error-custom', autoClose: 4000 });
      return;
    }

    setErrors({ title: false, content: false });
    const payload = {
      title,
      contentHtml,
      version,
      isActive,
    };
    try {
      await dispatch(createPolicy(payload)).unwrap();
      toast.success("Policy created successfully!", { className: 'toast-success-custom' });
      navigate("/admin/policies");
    } catch (error) {
      toast.error(error?.data?.reason || "Failed to create policy.", { className: 'toast-error-custom', autoClose: 4000 });
    }
  };

  if (!canEditPolicy) { return (<p className="perm-msg">You don’t have permission to create policies!</p>); }

  return (
    <>
      <div className="section-body policy-update">
        <div className="container-fluid">
          <Breadcrumb />
          <div className="d-flex justify-content-between mt-3 mb-4 align-middle">
            <h5 className="mb-3 mt-1 d-none-m">Create Policy</h5>
            <div className="d-flex align-items-end">
              <div className="policy-status-toggle">
                <input
                  type="radio"
                  id="publish"
                  name="policyStatus"
                  checked={isActive === true}
                  onChange={() => setIsActive(true)}
                />
                <label htmlFor="publish" className="publish m-0">
                  Publish
                </label>
                <input
                  type="radio"
                  id="draft"
                  name="policyStatus"
                  checked={isActive === false}
                  onChange={() => setIsActive(false)}
                />
                <label htmlFor="draft" className="draft m-0">
                  Draft
                </label>
              </div>
              <Button type="submit" form="policyCreateForm" className="primary-btn ml-4">
                Save Policy
              </Button>
            </div>
          </div>
          <div className="card mb-5">
            <div className="card-header">
              <Form id="policyCreateForm" onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Policy Title:</strong></Form.Label>
                  <Form.Control
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      if (errors.title) {
                        setErrors((prev) => ({ ...prev, title: false }));
                      }
                    }}
                    className={errors.title ? "is-invalid" : ""}
                  />
                  {errors.title && (
                    <div className="invalid-feedback">Policy title is required</div>
                  )}
                </Form.Group>
                {/* <Form.Group className="mb-3">
                <Form.Label>Version</Form.Label>
                <Form.Control value={version} onChange={(e) => setVersion(e.target.value)} />
              </Form.Group> */}
                <Form.Group className="mb-4">
                  <Form.Label><strong>Policy Content: </strong></Form.Label>
                  <div className={errors.content ? "is-invalid-editor" : ""}>
                    <Editor
                      value={contentHtml}
                      onEditorChange={(html) => {
                        setContentHtml(html);
                        if (errors.content) {
                          setErrors((prev) => ({ ...prev, content: false }));
                        }
                      }}
                      init={tinyConfig}
                      apiKey="x1zlwv4dd93x8wuuh7iovblba5p75zd4iwk50u953zh33s2p"
                    />
                  </div>
                  {errors.content && (
                    <div className="invalid-feedback d-block">
                      Policy content is required
                    </div>
                  )}
                </Form.Group>
              </Form>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
}
